<template>
  <div class="window-ranking page-shell">
    <div class="toolbar">
      <span class="toolbar-label">样本池</span>
      <el-select v-model="sample" size="small" style="width: 150px" @change="loadData">
        <el-option
          v-for="opt in sampleOptions"
          :key="opt.value"
          :label="opt.label"
          :value="opt.value"
        />
      </el-select>

      <span class="toolbar-label">排序区间</span>
      <el-select v-model="curWindow" size="small" style="width: 100px">
        <el-option
          v-for="w in windows"
          :key="w"
          :label="w + '日'"
          :value="w"
        />
      </el-select>

      <el-radio-group v-model="direction" size="small">
        <el-radio-button label="desc">涨幅榜</el-radio-button>
        <el-radio-button label="asc">跌幅榜</el-radio-button>
      </el-radio-group>

      <el-button
        type="primary"
        size="small"
        :loading="loading"
        icon="el-icon-refresh"
        style="margin-left: 8px"
        @click="loadData"
      >
        刷新
      </el-button>
      <el-dropdown trigger="click" @command="handleExportCommand">
        <el-button type="success" size="small" icon="el-icon-download">
          导出<i class="el-icon-arrow-down el-icon--right" />
        </el-button>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="all">导出当前榜单（{{ rankedRows.length }} 只）</el-dropdown-item>
          <el-dropdown-item command="selected" :disabled="!selectedRows.length">
            导出已选（{{ selectedRows.length }} 只）
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </div>

    <div class="toolbar toolbar--sub">
      <span class="toolbar-tip">
        最新交易日 {{ latestDate || '—' }} · 当前区间有效 {{ rankedRows.length }} 只
        <template v-if="excludedCount">· 已剔除疑似除权 {{ excludedCount }} 只</template>
        <template v-if="!hasLongWindow">· 数据自 2025-12-18 起，120/250 日区间待数据积累</template>
      </span>
      <span class="pager-size">
        每页
        <el-select v-model="pageSize" size="mini" style="width: 82px" @change="page = 1">
          <el-option :value="100" label="100 条" />
          <el-option :value="200" label="200 条" />
          <el-option :value="500" label="500 条" />
        </el-select>
      </span>
    </div>

    <el-alert
      v-if="error"
      :title="error"
      type="error"
      show-icon
      :closable="false"
      style="margin: 0 0 12px; text-align: left"
    />

    <div ref="tableShell" class="table-shell" v-loading="loading" element-loading-text="计算区间涨幅中...">
      <el-table
        v-if="displayRows.length"
        ref="rankTable"
        :data="displayRows"
        style="width: 100%"
        :height="tableHeight"
        :row-style="{ height: '34px', lineHeight: '34px' }"
        :cell-style="{ padding: '3px 0' }"
        stripe
        @selection-change="onSelectionChange"
        @sort-change="handleSortChange"
      >
        <el-table-column type="selection" width="44" fixed="left" />
        <el-table-column label="排名" width="60" fixed="left" align="right">
          <template slot-scope="{ row }">{{ row.rank }}</template>
        </el-table-column>
        <el-table-column prop="code" label="代码" width="84" fixed="left">
          <template slot-scope="{ row }">
            <el-link :href="stockstarCodeLink(row.code)" target="_blank" rel="noopener noreferrer">
              {{ row.code }}
            </el-link>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="名称" width="110" fixed="left" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <el-link :href="eastmoneyCodeLink(row)" target="_blank" rel="noopener noreferrer">
              {{ row.name }}
            </el-link>
          </template>
        </el-table-column>
        <el-table-column prop="industry" label="所属行业" min-width="100" show-overflow-tooltip />
        <el-table-column label="最新价" width="82" align="right">
          <template slot-scope="{ row }">{{ formatPrice(row.close) }}</template>
        </el-table-column>
        <el-table-column
          v-for="w in windows"
          :key="w"
          :prop="'w' + w"
          :label="w + '日%'"
          :width="w >= 100 ? 108 : 88"
          align="right"
          sortable="custom"
          :class-name="w === curWindow ? 'col-active' : ''"
        >
          <template slot-scope="{ row }">
            <el-tooltip
              v-if="row.exr.includes(w)"
              :content="'区间内疑似除权，' + w + '日数据可能失真'"
              placement="top"
            >
              <span class="exr-cell">{{ formatPct(row.pcts[w]) }} ⚠</span>
            </el-tooltip>
            <span
              v-else
              :class="[
                row.pcts[w] == null ? 'muted' : (row.pcts[w] >= 0 ? 'up' : 'down'),
                w === curWindow ? 'bold' : '',
              ]"
            >
              {{ row.pcts[w] == null ? '—' : formatPct(row.pcts[w]) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column width="70" align="right">
          <template slot="header">
            RPS
            <el-tooltip
              content="RPS（Relative Price Strength）：相对强度，即该股当前选中区间的涨幅在样本池内的百分位排名（0~100）。数值越大代表同期表现越强，如 RPS=90 表示涨幅超过样本池中 90% 的股票。"
              placement="top"
            >
              <i class="el-icon-question rps-tip" />
            </el-tooltip>
          </template>
          <template slot-scope="{ row }">
            <span class="rps-cell">{{ row.rps }}</span>
          </template>
        </el-table-column>
        <el-table-column label="除权" width="56" align="center">
          <template slot-scope="{ row }">
            <el-tooltip
              v-if="row.exr.length"
              :content="'疑似除权区间：' + row.exr.join('、') + ' 日'"
              placement="top"
            >
              <span class="exr-mark">⚠</span>
            </el-tooltip>
            <span v-else class="muted">-</span>
          </template>
        </el-table-column>
      </el-table>

      <div v-else-if="queried && !loading" class="empty-tip">
        当前样本池在 {{ curWindow }} 日区间没有可计算的股票。
      </div>

      <el-pagination
        v-if="rankedRows.length > pageSize"
        :current-page.sync="page"
        :page-size="pageSize"
        :total="rankedRows.length"
        layout="prev, pager, next, jumper, total"
        style="margin-top: 10px; text-align: right"
      />
    </div>
  </div>
</template>

<script>
import { fetchWithCredentials } from '../utils/http.js'
import { eastmoneyCodeLink, stockstarCodeLink } from '../utils/stockLink.js'
import * as XLSX from 'xlsx'

const WINDOWS = [1, 3, 5, 10, 20, 30, 60, 120, 250]

export default {
  name: 'WindowRanking',
  data() {
    return {
      windows: WINDOWS,
      sample: 'top500',
      curWindow: 5,
      direction: 'asc',
      items: [],
      counts: {},
      latestDate: '',
      selectedRows: [],
      loading: false,
      queried: false,
      error: '',
      page: 1,
      pageSize: 100,
      tableHeight: 560,
    }
  },
  computed: {
    sampleOptions() {
      const labels = {
        all: '全市场',
        top500: 'Top500',
        p75: 'P75严格',
        p60: 'P60宽松',
      }
      return ['all', 'top500', 'p60', 'p75'].map((value) => {
        const n = this.counts[value]
        return {
          value,
          label: n != null && n !== '' ? `${labels[value]} ${n}` : labels[value],
        }
      })
    },
    /** 当前区间可参与排序的股票（有值且未标除权），按方向排序后编号 + RPS */
    rankedRows() {
      const w = this.curWindow
      const valid = this.items.filter(
        (it) => it.pcts[w] != null && !it.exr.includes(w)
      )
      const desc = this.direction === 'desc'
      valid.sort((a, b) => (desc ? b.pcts[w] - a.pcts[w] : a.pcts[w] - b.pcts[w]))
      const n = valid.length
      return valid.map((it, i) => ({
        ...it,
        rank: i + 1,
        // RPS：区间涨幅在样本内的百分位（0~100，越大越强），与榜单方向无关
        rps: n > 1 ? Math.round((100 * (n - 1 - i)) / (n - 1)) : 100,
      }))
    },
    excludedCount() {
      const w = this.curWindow
      return this.items.filter(
        (it) => it.pcts[w] != null && it.exr.includes(w)
      ).length
    },
    displayRows() {
      const start = (this.page - 1) * this.pageSize
      return this.rankedRows.slice(start, start + this.pageSize)
    },
    hasLongWindow() {
      return this.items.some((it) => it.pcts[120] != null)
    },
  },
  watch: {
    curWindow() {
      this.page = 1
      this.clearSelection()
    },
    direction() {
      this.page = 1
      this.clearSelection()
    },
    sample() {
      this.page = 1
    },
  },
  async created() {
    await this.loadData()
    await this.loadCounts()
  },
  mounted() {
    this.updateTableHeight()
    window.addEventListener('resize', this.updateTableHeight, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateTableHeight)
  },
  methods: {
    eastmoneyCodeLink,
    stockstarCodeLink,
    updateTableHeight() {
      this.$nextTick(() => {
        const shell = this.$refs.tableShell
        if (!shell) return
        const top = shell.getBoundingClientRect().top
        const height = Math.max(280, Math.floor(window.innerHeight - top - 50))
        if (height !== this.tableHeight) {
          this.tableHeight = height
        }
        this.$nextTick(() => {
          const table = this.$refs.rankTable
          if (table?.doLayout) table.doLayout()
        })
      })
    },
    async loadData() {
      this.loading = true
      this.error = ''
      this.queried = true
      try {
        const { res, data } = await fetchWithCredentials(
          `/api/window-ranking?sample=${this.sample}`
        )
        if (!res.ok) {
          throw new Error(data.error || `波段雷达接口失败 ${res.status}`)
        }
        this.latestDate = data.latestDate || ''
        this.items = data.items || []
      } catch (e) {
        this.items = []
        this.error = e.message || String(e)
      } finally {
        this.loading = false
      }
    },
    async loadCounts() {
      // 四个样本池数量（失败不影响主列表）；接口异常时不显示数量，避免出现"看着合理其实是错的"数值
      try {
        const results = await Promise.all(
          ['all', 'top500', 'p60', 'p75'].map((s) =>
            fetchWithCredentials(`/api/window-ranking?sample=${s}`).then(({ res, data }) => ({
              s,
              n: res.ok ? data.count ?? (data.items || []).length : null,
            }))
          )
        )
        const counts = {}
        results.forEach(({ s, n }) => { counts[s] = n })
        this.counts = counts
      } catch (e) {
        this.counts = { [this.sample]: this.items.length }
      }
    },
    handleSortChange({ prop, order }) {
      if (!prop || !prop.startsWith('w')) return
      const w = Number(prop.slice(1))
      if (w === this.curWindow) {
        // 点击当前区间列头 → 切换涨/跌幅榜
        this.direction = order === 'ascending' ? 'asc' : 'desc'
      } else {
        // 点击参考列头 → 切换排序区间
        this.curWindow = w
      }
    },
    onSelectionChange(rows) {
      this.selectedRows = rows || []
    },
    clearSelection() {
      this.$nextTick(() => {
        const table = this.$refs.rankTable
        if (table && table.clearSelection) table.clearSelection()
      })
    },
    handleExportCommand(command) {
      if (command === 'all') {
        if (!this.rankedRows.length) {
          this.$message.warning('当前没有可导出的数据')
          return
        }
        this.exportRows(this.rankedRows, `${this.curWindow}日${this.direction === 'desc' ? '涨幅榜' : '跌幅榜'}全部`)
      } else if (command === 'selected') {
        if (!this.selectedRows.length) {
          this.$message.warning('请先在表格中勾选要导出的行')
          return
        }
        const codes = new Set(this.selectedRows.map((r) => r.code))
        const sorted = this.rankedRows.filter((r) => codes.has(r.code))
        this.exportRows(sorted, `${this.curWindow}日已选${sorted.length}只`)
      }
    },
    exportRows(rows, label) {
      const headers = [
        '排名', '代码', '名称', '所属行业', '最新价',
        ...WINDOWS.map((w) => w + '日(%)'),
        'RPS', '疑似除权区间(日)',
      ]
      const data = rows.map((row) => {
        const item = {
          '排名': row.rank,
          '代码': row.code,
          '名称': row.name,
          '所属行业': row.industry || '',
          '最新价': this.round2(row.close),
        }
        WINDOWS.forEach((w) => {
          item[w + '日(%)'] = row.pcts[w] == null ? '' : this.round2(row.pcts[w])
        })
        item['RPS'] = row.rps
        item['疑似除权区间(日)'] = row.exr.length ? row.exr.join('、') : ''
        return item
      })
      const sheet = XLSX.utils.json_to_sheet(data, { header: headers })
      sheet['!cols'] = headers.map((h) => ({ wch: h.length > 5 ? 11 : 8 }))
      const book = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(book, sheet, '波段雷达')
      XLSX.writeFile(book, `波段雷达_${this.sampleLabel}_${this.latestDate}_${label}.xlsx`)
      this.$message.success(`已导出 ${rows.length} 只（${label}）`)
    },
    sampleLabel() {
      return { all: '全市场', top500: 'Top500', p75: 'P75严格', p60: 'P60宽松' }[this.sample] || this.sample
    },
    round2(v) {
      if (v == null || v === '') return ''
      const n = Number(v)
      return Number.isFinite(n) ? Number(n.toFixed(2)) : ''
    },
    formatPrice(v) {
      if (v == null || v === '') return ''
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(2) : ''
    },
    formatPct(v) {
      if (v == null || v === '') return ''
      return Number(v).toFixed(2)
    },
  },
}
</script>

<style scoped>
.window-ranking {
  width: 100%;
  min-width: 0;
}

.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin: 0 0 10px;
  text-align: left;
}

.toolbar--sub {
  justify-content: space-between;
  margin-bottom: 10px;
}

.toolbar-label {
  font-size: 13px;
  color: #606266;
  font-weight: 500;
}

.toolbar-tip {
  font-size: 12px;
  color: #909399;
}

.pager-size {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #909399;
}

.empty-tip {
  margin: 0 0 12px;
  text-align: left;
  font-size: 13px;
  color: #909399;
  padding: 16px;
}

.up {
  color: #f56c6c;
}

.down {
  color: #67c23a;
}

.bold {
  font-weight: 600;
}

.muted {
  color: #c0c4cc;
}

.rps-cell {
  font-weight: 500;
  color: #303133;
}

.rps-tip {
  color: #c0c4cc;
  cursor: help;
  margin-left: 2px;
  font-size: 14px;
}

.rps-tip:hover {
  color: #909399;
}

.exr-cell {
  color: #c0c4cc;
  cursor: help;
}

.exr-mark {
  color: #e6a23c;
  cursor: help;
}

.table-shell {
  min-height: 0;
}

.table-shell /deep/ .el-table th > .cell {
  white-space: nowrap;
  line-height: 23px;
}

.table-shell /deep/ .col-active {
  background: #f5f9ff;
}
</style>
